/**
 * Admin Client Health Overview
 *
 * Global view of all client health scores across the portfolio.
 * Computes scores on-demand for agents without recent snapshots.
 */

import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HeartPulse, Loader2, Heart, AlertTriangle, ShieldAlert, ShieldCheck, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import ClientHealthBadge from "@/components/am/ClientHealthBadge";
import {
  computeDelivery,
  computeEngagement,
  computeBilling,
  computeContract,
  computeOverall,
  getStatus,
  getRiskFactors,
} from "@/hooks/useHealthScore";
import type { ClientContract, ClientLead } from "@/hooks/useAMClientDetail";
import type { Json } from "@/integrations/supabase/types";

type HealthStatus = "healthy" | "neutral" | "at_risk" | "critical";
type FilterOption = "all" | "healthy" | "neutral" | "at_risk_critical";

interface HealthRow {
  agent_id: string;
  overall_score: number;
  health_status: HealthStatus;
  score_delta_7d: number | null;
  computed_at: string;
  agent_name: string;
  agent_company: string | null;
  agent_vertical: string;
  agent_tier: string;
  am_name: string | null;
  is_computed_live: boolean;
}

export default function AdminClientHealth() {
  const [rows, setRows] = useState<HealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>("all");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch all parallel data
      const [agentsRes, healthRes, contractsRes, leadsRes, amsRes, adminRes] = await Promise.all([
        supabase
          .from("agents")
          .select("id, name, company, vertical, tier, is_active, is_demo, assigned_am_id")
          .eq("is_demo", false)
          .eq("is_active", true),
        supabase
          .from("client_health_scores")
          .select("agent_id, overall_score, health_status, score_delta_7d, computed_at")
          .order("computed_at", { ascending: false })
          .limit(2000),
        supabase
          .from("contracts")
          .select("id, agent_id, contract_type, status, lead_types, leads_delivered, total_leads_committed, cycle_start, cycle_end, paused_at, paused_reason, cancel_at_period_end, priority_tier, created_at, notes")
          .in("status", ["active", "paused", "at_risk", "fulfilled"]),
        supabase
          .from("leads")
          .select("id, routed_to_agent_id, agent_status, lead_type_name, state, status, disposition, estimated_value, charged_amount, created_at, agent_contacted_at, lead_profile")
          .gte("created_at", Date.now() - 90 * 24 * 60 * 60 * 1000),
        supabase
          .from("account_managers")
          .select("id, full_name"),
        supabase
          .from("admin_users")
          .select("user_id"),
      ]);

      // 2. Check for query errors
      const queryErrors = [
        agentsRes.error && `Agents: ${agentsRes.error.message}`,
        healthRes.error && `Health scores: ${healthRes.error.message}`,
        contractsRes.error && `Contracts: ${contractsRes.error.message}`,
        leadsRes.error && `Leads: ${leadsRes.error.message}`,
      ].filter(Boolean);

      if (queryErrors.length > 0) {
        console.error("[AdminClientHealth] Query errors:", queryErrors);
        setError(`Failed to load data: ${queryErrors.join("; ")}`);
        // Continue with whatever data we have
      }

      const agents = agentsRes.data || [];
      const scores = healthRes.data || [];
      const contracts = contractsRes.data || [];
      const leads = leadsRes.data || [];
      const ams = amsRes.data || [];
      const internalUserIds = new Set((adminRes.data ?? []).map((r) => r.user_id));

      // 3. Build lookup maps
      const amMap = new Map(ams.map((am) => [am.id, am.full_name]));
      const clientAgents = agents.filter((a) => !internalUserIds.has(a.id));

      // Group contracts and leads by agent
      const contractsByAgent = new Map<string, ClientContract[]>();
      for (const c of contracts) {
        if (!c.agent_id) continue;
        const list = contractsByAgent.get(c.agent_id) || [];
        list.push(c as unknown as ClientContract);
        contractsByAgent.set(c.agent_id, list);
      }

      const leadsByAgent = new Map<string, ClientLead[]>();
      for (const l of leads) {
        const agentId = (l as Record<string, unknown>).routed_to_agent_id as string;
        if (!agentId) continue;
        const list = leadsByAgent.get(agentId) || [];
        list.push(l as unknown as ClientLead);
        leadsByAgent.set(agentId, list);
      }

      // 4. Deduplicate scores: latest per agent_id
      const latestScoreByAgent = new Map<string, (typeof scores)[0]>();
      for (const s of scores) {
        if (!latestScoreByAgent.has(s.agent_id)) {
          latestScoreByAgent.set(s.agent_id, s);
        }
      }

      // 5. Build rows — base on AGENTS, not scores (Bug 8 fix)
      const sixHoursMs = 6 * 60 * 60 * 1000;
      const merged: HealthRow[] = [];
      const scoresToInsert: Array<{
        agent_id: string;
        overall_score: number;
        health_status: string;
        delivery_score?: number | null;
        engagement_score?: number | null;
        billing_score?: number | null;
        contract_score?: number | null;
        score_delta_7d?: number | null;
        score_delta_30d?: number | null;
        risk_factors?: Json | null;
      }> = [];

      for (const agent of clientAgents) {
        const existingScore = latestScoreByAgent.get(agent.id);
        const agentContracts = contractsByAgent.get(agent.id) || [];
        const agentLeads = leadsByAgent.get(agent.id) || [];

        // Check if the stored score is fresh (< 6 hours old)
        const scoreFresh = existingScore?.computed_at
          ? Date.now() - new Date(existingScore.computed_at).getTime() < sixHoursMs
          : false;

        let scoreData: { overall: number; status: HealthStatus; delta7d: number | null; computedAt: string; isLive: boolean };

        if (scoreFresh && existingScore) {
          // Use stored score
          scoreData = {
            overall: existingScore.overall_score,
            status: existingScore.health_status as HealthStatus,
            delta7d: existingScore.score_delta_7d,
            computedAt: existingScore.computed_at || "",
            isLive: false,
          };
        } else {
          // Compute live score from contracts + leads
          const del = computeDelivery(agentContracts);
          const eng = computeEngagement(agentLeads, agentContracts);
          const bil = computeBilling(agentContracts);
          const con = computeContract(agentContracts);
          const overall = computeOverall(del, eng, bil, con);
          const status = getStatus(overall);
          const delta7d = existingScore ? overall - existingScore.overall_score : null;
          const riskFactors = getRiskFactors(del, eng, agentContracts);

          scoreData = {
            overall,
            status,
            delta7d,
            computedAt: new Date().toISOString(),
            isLive: true,
          };

          // Queue for batch insert
          scoresToInsert.push({
            agent_id: agent.id,
            overall_score: overall,
            health_status: status,
            delivery_score: del,
            engagement_score: eng,
            billing_score: bil,
            contract_score: con,
            score_delta_7d: delta7d,
            score_delta_30d: null,
            risk_factors: riskFactors as unknown as Json,
          });
        }

        merged.push({
          agent_id: agent.id,
          overall_score: scoreData.overall,
          health_status: scoreData.status,
          score_delta_7d: scoreData.delta7d,
          computed_at: scoreData.computedAt,
          agent_name: agent.name || "Unknown",
          agent_company: agent.company || null,
          agent_vertical: agent.vertical || "unknown",
          agent_tier: agent.tier || "Bronze",
          am_name: agent.assigned_am_id ? amMap.get(agent.assigned_am_id) || null : null,
          is_computed_live: scoreData.isLive,
        });
      }

      // 6. Batch insert computed scores (fire-and-forget)
      if (scoresToInsert.length > 0) {
        supabase.from("client_health_scores").insert(scoresToInsert).then(({ error: insertErr }) => {
          if (insertErr) {
            console.error("[AdminClientHealth] Failed to persist computed scores:", insertErr.message);
          } else {
            console.log(`[AdminClientHealth] Persisted ${scoresToInsert.length} computed health scores`);
          }
        });
      }

      // 7. Sort by score ascending (worst first)
      merged.sort((a, b) => a.overall_score - b.overall_score);
      setRows(merged);
    } catch (err) {
      console.error("[AdminClientHealth] Error:", err);
      setError("An unexpected error occurred while loading health data.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const counts = {
    healthy: rows.filter((r) => r.health_status === "healthy").length,
    neutral: rows.filter((r) => r.health_status === "neutral").length,
    at_risk: rows.filter((r) => r.health_status === "at_risk").length,
    critical: rows.filter((r) => r.health_status === "critical").length,
  };

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "healthy") return r.health_status === "healthy";
    if (filter === "neutral") return r.health_status === "neutral";
    if (filter === "at_risk_critical")
      return r.health_status === "at_risk" || r.health_status === "critical";
    return true;
  });

  const verticalLabels: Record<string, string> = {
    life_insurance: "Life Insurance",
    mortgage: "Mortgage",
  };

  const tierColors: Record<string, string> = {
    Elite: "bg-purple-100 text-purple-700 border-purple-200",
    Gold: "bg-amber-100 text-amber-700 border-amber-200",
    Silver: "bg-slate-100 text-slate-700 border-slate-200",
    Bronze: "bg-orange-100 text-orange-700 border-orange-200",
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
          <span className="text-muted-foreground">Loading health scores...</span>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <HeartPulse className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">Client Health Overview</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Monitor client health scores across your portfolio
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {rows.length === 0 && !error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HeartPulse className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              No active clients found. Health scores will appear once agents have active contracts.
            </p>
          </CardContent>
        </Card>
      ) : rows.length > 0 ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Healthy</CardTitle>
                <ShieldCheck className="h-3.5 w-3.5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{counts.healthy}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Neutral</CardTitle>
                <Heart className="h-3.5 w-3.5 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{counts.neutral}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">At Risk</CardTitle>
                <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{counts.at_risk}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Critical</CardTitle>
                <ShieldAlert className="h-3.5 w-3.5 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{counts.critical}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filter Bar */}
          <div className="flex items-center gap-2 mb-4">
            {(
              [
                { key: "all", label: "All" },
                { key: "healthy", label: "Healthy" },
                { key: "neutral", label: "Neutral" },
                { key: "at_risk_critical", label: "At Risk + Critical" },
              ] as { key: FilterOption; label: string }[]
            ).map((f) => (
              <Button
                key={f.key}
                size="sm"
                variant={filter === f.key ? "default" : "outline"}
                onClick={() => setFilter(f.key)}
                className="text-xs"
              >
                {f.label}
              </Button>
            ))}
          </div>

          {/* Table */}
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-3 font-medium">Client Name</th>
                      <th className="pb-3 font-medium">Company</th>
                      <th className="pb-3 font-medium">Vertical</th>
                      <th className="pb-3 font-medium">Tier</th>
                      <th className="pb-3 font-medium">AM</th>
                      <th className="pb-3 font-medium">Health Score</th>
                      <th className="pb-3 font-medium">7d Trend</th>
                      <th className="pb-3 font-medium">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <tr
                        key={row.agent_id}
                        className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="py-3 font-medium text-foreground">{row.agent_name}</td>
                        <td className="py-3 text-muted-foreground">{row.agent_company || "—"}</td>
                        <td className="py-3">{verticalLabels[row.agent_vertical] || row.agent_vertical}</td>
                        <td className="py-3">
                          <Badge className={tierColors[row.agent_tier] || ""} variant="outline">
                            {row.agent_tier}
                          </Badge>
                        </td>
                        <td className="py-3 text-muted-foreground">{row.am_name || "—"}</td>
                        <td className="py-3">
                          <ClientHealthBadge
                            score={row.overall_score}
                            status={row.health_status}
                            size="sm"
                          />
                        </td>
                        <td className="py-3">
                          {row.score_delta_7d !== null && row.score_delta_7d !== 0 ? (
                            <span className={row.score_delta_7d > 0 ? "text-green-600 text-xs" : "text-red-600 text-xs"}>
                              {row.score_delta_7d > 0 ? `↑${row.score_delta_7d}` : `↓${Math.abs(row.score_delta_7d)}`}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-muted-foreground text-xs">
                          {row.computed_at ? format(new Date(row.computed_at), "MMM d, yyyy") : "—"}
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-muted-foreground">
                          No clients match the selected filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </AdminLayout>
  );
}
